import React, { useRef } from 'react';
import { BlockText, CardGrid, DropDown, Flexbox, StyledInput } from '@morressier/ts';
import EventCard from 'components/EventCard';
import Paging from 'components/Paging';
import { useRouter } from 'next/router';
import qs from 'qs';
import { getPage } from 'utils/pagingHelper';
import * as ContainerStyles from '../styles';

type Props = {
  events: OrganizationEventsResponseV3;
  organizationId: string;
  // eslint-disable-next-line react/require-default-props
  searchTerm?: string;
  // eslint-disable-next-line react/require-default-props
  sortBy?: Record<'start_date' | 'name', 'asc' | 'desc'>;
};

const SearchForm = (props: { defaultValue: string; onSearch: (searchTerm: string) => void }) => {
  const ref = useRef<HTMLInputElement>(null);
  // const [loading, setLoading] = useState(false);

  return (
    <form
      style={{ width: 360 }}
      onSubmit={e => {
        e.preventDefault();
        // setLoading(true);
        props.onSearch(ref.current?.value ?? '');
      }}
    >
      <StyledInput
        ref={ref}
        placeholder="Search"
        defaultValue={props.defaultValue}
        // disabled={loading}
      />
    </form>
  );
};

const getSortString = (sortObj: Props['sortBy']) => {
  const defaultSort = 'start_date-desc';
  if (!sortObj) {
    return defaultSort;
  }
  if (Object.keys(sortObj).length > 0) {
    const sortKey = Object.keys(sortObj)[0];
    const sortValue = sortObj[sortKey];

    return `${sortKey}-${sortValue}`;
  }

  return defaultSort;
};

const buildSearchUrl = (
  organizationId: string,
  searchTerm: string | undefined,
  page: number,
  field: string,
  direction: string,
) =>
  `/organizations/${organizationId}/events/search?${qs.stringify({
    q: searchTerm,
    page,
    sortBy: { [field]: direction },
  })}`;

const Events = (props: Props) => {
  const { events, sortBy, searchTerm } = props;
  const router = useRouter();

  const sortValue = getSortString(sortBy);
  const [sortField, sortDirection] = String(sortValue).split('-');
  const page = Number(router.query.page ?? 1);
  const isSearch = searchTerm && sortValue;

  const totalPages = Math.max(1, Math.ceil(events.collection.total / events.collection.limit));

  return (
    <ContainerStyles.PageContainer withTopMargin>
      <BlockText fontWeight="bold" size="h2_new" color="secondaryBrand" className="mb2">
        Conferences
      </BlockText>

      <Flexbox className="mr2 mb2" style={{ gap: '1rem' }}>
        <SearchForm
          defaultValue={searchTerm ?? ''}
          onSearch={newSearch =>
            router.push(
              newSearch
                ? `/organizations/${props.organizationId}/events/search?q=${newSearch}`
                : `/organizations/${props.organizationId}/events`,
            )
          }
        />
        <DropDown
          textAlign="left"
          options={[
            { value: 'start_date-desc', text: 'Sort by date (latest first)' },
            { value: 'start_date-asc', text: 'Sort by date (oldest first)' },
            { value: 'name-asc', text: 'Sort by name (A - Z)' },
            { value: 'name-desc', text: 'Sort by name (Z - A)' },
          ]}
          onChange={({ value }) => {
            const [field, direction] = String(value).split('-');

            router.push(buildSearchUrl(props.organizationId, searchTerm, page, field, direction));
          }}
          initialValue={sortValue}
        />
      </Flexbox>

      <BlockText color="grey" size="body1_new" className="mb2">
        {events.collection.total} conferences
      </BlockText>

      <CardGrid distribution="grid">
        {events.items.map(event => (
          <EventCard
            key={event.id}
            id={event.id}
            title={event.name}
            date={event.start_date}
            location={event.location ?? [event.country, event.city].filter(Boolean).join(', ')}
            image={event.banner_url ?? event.logo_url ?? ''}
          />
        ))}
      </CardGrid>

      {isSearch ? (
        <Paging
          totalPages={totalPages}
          currentPage={page}
          pageRouteBuilder={_page =>
            buildSearchUrl(props.organizationId, searchTerm, _page, sortField, sortDirection)
          }
        />
      ) : (
        <Paging
          totalPages={totalPages}
          currentPage={getPage(events.collection.offset, events.collection.limit)}
          pageRouteBuilder={_page => `/organizations/${props.organizationId}/events/${_page}`}
        />
      )}
    </ContainerStyles.PageContainer>
  );
};

export default Events;
